import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import {
  StepBasics,
  StepDestination,
  StepExam,
  StepIntake,
  StepNotes,
} from "./ApplyFormSteps";
import ApplyFormSuccess from "./ApplyFormSuccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Basics", "Destination", "Exam status", "Intake", "Confirm"];
const TOTAL_STEPS = STEPS.length;
const LAST_STEP = TOTAL_STEPS - 1;

const INITIAL_DATA = {
  name: "",
  email: "",
  whatsapp: "",
  city: "",
  destination: "",
  exam: "",
  score: "",
  intakeYear: "",
  intakeSeason: "",
  notes: "",
};

const isStepValid = (step, data) => {
  if (step === 0) {
    return Boolean(data.name && data.email && data.whatsapp && data.city);
  }
  if (step === 1) return Boolean(data.destination);
  if (step === 2) return Boolean(data.exam);
  if (step === 3) return Boolean(data.intakeYear && data.intakeSeason);
  return true;
};

const buildPayload = (data) => ({
  name: data.name,
  email: data.email,
  whatsapp: data.whatsapp,
  city: data.city,
  destination: data.destination || null,
  exam: data.exam || null,
  score: data.score || null,
  intake_year: data.intakeYear || null,
  intake_season: data.intakeSeason || null,
  notes: data.notes || null,
});

const renderStep = (step, data, setField) => {
  if (step === 0) return <StepBasics data={data} setField={setField} />;
  if (step === 1) return <StepDestination data={data} setField={setField} />;
  if (step === 2) return <StepExam data={data} setField={setField} />;
  if (step === 3) return <StepIntake data={data} setField={setField} />;
  return <StepNotes data={data} setField={setField} />;
};

const SubmitButtonLabel = ({ isLast, submitting }) => {
  if (!isLast) {
    return (
      <>
        Next <ArrowRight className="h-4 w-4" />
      </>
    );
  }
  if (submitting) {
    return (
      <>
        <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
      </>
    );
  }
  return (
    <>
      <Sparkles className="h-4 w-4" /> Submit application
    </>
  );
};

const ApplyForm = () => {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(INITIAL_DATA);

  const setField = useCallback(
    (key) => (e) =>
      setData((prev) => ({ ...prev, [key]: e?.target?.value ?? e })),
    []
  );

  const progress = useMemo(() => ((step + 1) / TOTAL_STEPS) * 100, [step]);
  const canNext = useMemo(() => isStepValid(step, data), [step, data]);

  const submitApplication = useCallback(async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, buildPayload(data));
    } catch (err) {
      // Graceful fallback: keep showing thank-you + WhatsApp CTA even on network failure
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error("Application submit failed:", err);
      }
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  }, [data]);

  const handleNext = useCallback(() => {
    if (step < LAST_STEP) {
      setStep((s) => s + 1);
    } else {
      submitApplication();
    }
  }, [step, submitApplication]);

  const handleBack = useCallback(
    () => setStep((s) => Math.max(0, s - 1)),
    []
  );

  const handleReset = useCallback(() => {
    setSubmitted(false);
    setStep(0);
  }, []);

  const isLast = step === LAST_STEP;

  return (
    <section id="apply" className="py-14 md:py-24 bg-[#0b1220]">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center text-red-400 text-[11.5px] tracking-[0.22em] uppercase font-medium">
          Apply Free · 5 quick steps
        </div>
        <h2 className="mt-3 text-center text-white text-[34px] md:text-[42px] font-semibold leading-[1.1] tracking-tight">
          Claim your IELTS / TOEFL / PTE{" "}
          <span className="text-amber-400">Exam Fee Cashback</span>
        </h2>
        <p className="mt-4 text-center text-slate-300">
          Takes under 2 minutes. No fees. We'll WhatsApp you within working hours to confirm next steps.
        </p>

        <div className="mt-10 rounded-3xl border border-white/8 bg-[#0e1626]/80 p-6 md:p-8">
          {submitted ? (
            <ApplyFormSuccess name={data.name} onReset={handleReset} />
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="text-[10.5px] tracking-[0.18em] uppercase text-slate-400">
                  Step {step + 1} of {TOTAL_STEPS}
                </div>
                <div className="text-[12.5px] text-slate-300">
                  {Math.round(progress)}% complete
                </div>
              </div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-red-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>

              {renderStep(step, data, setField)}

              <div className="mt-8 flex items-center justify-between border-t border-white/8 pt-5">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={step === 0}
                  className="h-10 rounded-full bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white disabled:opacity-40 px-4 inline-flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!canNext || submitting}
                  className="h-10 rounded-full bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 px-5 inline-flex items-center gap-2"
                >
                  <SubmitButtonLabel isLast={isLast} submitting={submitting} />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ApplyForm;
